import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { CheckInChatInterface } from "@/components/CheckInChatInterface";
import {
	PageHeader,
	MoodChart,
	PaginationControls,
	CheckInCard,
	EmptyState,
	DeleteConfirmDialog,
} from "./components";
import { useCheckIns, useChartData, useDeleteCheckIn } from "./hooks";
import type { DailyCheckIn } from "./types";

export function AllCheckIns() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const checkInRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

	const { checkIns, loading, page, setPage, totalCount, pageSize, refetch } =
		useCheckIns(user?.id);

	const chartData = useChartData(user?.id);

	const {
		deleteDialogOpen,
		setDeleteDialogOpen,
		deleting,
		handleDeleteCheckIn,
		confirmDeleteCheckIn,
		cancelDeleteCheckIn,
	} = useDeleteCheckIn({
		userId: user?.id,
		page,
		setPage,
		totalCount,
		pageSize,
		checkInsLength: checkIns.length,
		refetch,
	});

	const handleEditCheckIn = (
		checkIn: DailyCheckIn,
		event: React.MouseEvent,
	) => {
		event.preventDefault();
		event.stopPropagation();
		navigate(`/check-in/edit/${checkIn.id}`);
	};

	const handleChartClick = (data: any) => {
		if (data?.activePayload?.[0]) {
			const checkInId = data.activePayload[0].payload.checkInId;
			const checkInElement = checkInRefs.current[checkInId];

			if (checkInElement) {
				checkInElement.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});

				checkInElement.style.boxShadow = "0 0 20px rgba(59, 130, 246, 0.5)";
				setTimeout(() => {
					checkInElement.style.boxShadow = "";
				}, 2000);
			}
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
				<div className="container mx-auto px-4 py-8 max-w-7xl">
					<div className="flex items-center justify-center h-64">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
			<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
				<PageHeader />

				<div className="space-y-6 sm:space-y-8">
					<MoodChart chartData={chartData} onChartClick={handleChartClick} />

					<div>
						<CheckInChatInterface />
					</div>

					<PaginationControls
						page={page}
						setPage={setPage}
						totalCount={totalCount}
						pageSize={pageSize}
					/>

					{checkIns.length > 0 ? (
						<div className="space-y-4">
							{checkIns.map((checkIn) => (
								<CheckInCard
									key={checkIn.id}
									checkIn={checkIn}
									onEdit={handleEditCheckIn}
									onDelete={handleDeleteCheckIn}
									refCallback={(el) => (checkInRefs.current[checkIn.id] = el)}
								/>
							))}
						</div>
					) : (
						<EmptyState />
					)}
				</div>

				<DeleteConfirmDialog
					open={deleteDialogOpen}
					onOpenChange={setDeleteDialogOpen}
					onConfirm={confirmDeleteCheckIn}
					onCancel={cancelDeleteCheckIn}
					deleting={deleting}
				/>
			</div>
		</div>
	);
}
